import { AppWindow, Check, ShieldBan, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { relative } from '../lib/format';
import type { Child } from '../lib/types';
import { useParent } from './ParentContext';
import { childHas } from './Plans';

interface Category {
  id: string;
  label: string;
  emoji: string;
  description: string;
  apps: { name: string; windows: string[]; mac: string[] }[];
}
interface SeenApp {
  platform: 'windows' | 'mac';
  process: string;
  name: string;
  lastSeen: number;
}
type SingleApp = { name: string; process: string; platform: 'windows' | 'mac' };

const same = (a: SingleApp, b: { process: string; platform: string }) => a.process.toLowerCase() === b.process.toLowerCase() && a.platform === b.platform;

/**
 * Block during school time (#290, #334), per child: whole categories of apps and websites (games, chat, social media,
 * video, music), and any single app the ClassGuardian app has seen on this child's computers.
 */
/** What was just added for this child, to add for the ticked siblings too ("Also for", #765). */
export type AlsoAdd = (change: { blockApp?: SingleApp; approveApp?: SingleApp; blockCategory?: string }) => void;

export function BlockAppsCard({ child, also }: { child: Child; also?: AlsoAdd }) {
  if (!childHas(child, 'app_blocking')) return <GamesOnlyCard child={child} also={also} />;
  return <BlockAppsFull child={child} also={also} />;
}

/** Without App blocking in the plan (#99): blocking games stays available to everyone. */
function GamesOnlyCard({ child, also }: { child: Child; also?: AlsoAdd }) {
  const { replaceChild, toast } = useParent();
  const [busy, setBusy] = useState(false);
  const on = (child.blockCategories ?? []).includes('games') || !!child.blockGames;
  async function set(next: boolean) {
    setBusy(true);
    try {
      replaceChild(await api.patch<Child>(`/api/children/${child.id}`, { blockGames: next }));
      if (next) also?.({ blockCategory: 'games' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card stack">
      <h2>Block apps</h2>
      <label className="check">
        <input type="checkbox" checked={on} disabled={busy} onChange={(e) => set(e.target.checked)} />
        <span>
          <strong>Block games during school time</strong>
          <span className="muted small block">Game apps are closed and game sites are blocked while it’s school time.</span>
        </span>
      </label>
      <p className="muted small">Blocking chat, social, video and music apps, or any single app, is part of App blocking, which isn’t in your plan.</p>
    </section>
  );
}

function BlockAppsFull({ child, also }: { child: Child; also?: AlsoAdd }) {
  const { replaceChild, toast } = useParent();
  const [categories, setCategories] = useState<Category[]>([]);
  const [seen, setSeen] = useState<SeenApp[] | null>(null);
  const [busy, setBusy] = useState(false);
  const chosen = child.blockCategories ?? [];
  const single = child.blockedApps ?? [];
  const learning = child.learningApps ?? [];

  useEffect(() => {
    api
      .get<{ categories: Category[]; seen: SeenApp[] }>(`/api/children/${child.id}/apps`)
      .then((r) => {
        setCategories(r.categories);
        setSeen(r.seen);
      })
      .catch((e) => toast({ text: (e as Error).message, tone: 'alert' }));
  }, [child.id, toast]);

  async function save(patch: { blockCategories?: string[]; blockedApps?: SingleApp[]; learningApps?: SingleApp[] }, added?: Parameters<AlsoAdd>[0]) {
    setBusy(true);
    try {
      replaceChild(await api.patch<Child>(`/api/children/${child.id}`, patch));
      if (added) also?.(added);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  // Adding (never removing) also goes to the ticked siblings (#765).
  const toggleCategory = (id: string) =>
    chosen.includes(id) ? save({ blockCategories: chosen.filter((c) => c !== id) }) : save({ blockCategories: [...chosen, id] }, { blockCategory: id });
  const toggleApp = (a: SingleApp) =>
    single.some((s) => same(s, a)) ? save({ blockedApps: single.filter((s) => !same(s, a)) }) : save({ blockedApps: [...single, a] }, { blockApp: a });
  // A learning app (#443) is never closed, and its time counts toward attendance. Marking one also unblocks it.
  const toggleLearning = (a: SingleApp) =>
    learning.some((s) => same(s, a))
      ? save({ learningApps: learning.filter((s) => !same(s, a)) })
      : save({ learningApps: [...learning, a], blockedApps: single.filter((s) => !same(s, a)) }, { approveApp: a });

  // Seen apps first (newest first), then blocked apps that haven't been seen lately, so every blocked app can be unblocked.
  const rows: (SingleApp & { lastSeen?: number })[] = [
    ...(seen ?? []),
    ...single.filter((s) => !(seen ?? []).some((a) => same(s, a))),
    ...learning.filter((s) => !(seen ?? []).some((a) => same(s, a)) && !single.some((b) => same(b, s))),
  ];
  // Apps a blocked category already covers (by process or display name) are shown as such rather than as a switch.
  const coveredBy = (a: SingleApp) =>
    categories.find(
      (c) =>
        chosen.includes(c.id) &&
        c.apps.some(
          (x) =>
            x.name.toLowerCase() === a.name.toLowerCase() || (a.platform === 'mac' ? x.mac : x.windows).some((p) => p.toLowerCase() === a.process.toLowerCase()),
        ),
    );

  return (
    <section className="card stack">
      <h2 className="with-icon">
        <ShieldBan size={18} /> Block during school time
      </h2>
      <p className="muted small">
        Pick what {child.name} can’t use during school time. Each category blocks its websites (through the browser extension) and closes its apps on{' '}
        {child.name}’s Windows or Mac computer, with a friendly “after school” note for {child.name} and an alert for you. A site you add as a learning
        site above stays allowed.
      </p>
      <p className="muted small">
        <ShieldCheck size={13} className="inline-icon" /> <strong>Approve</strong> an app below and it’s never closed, even when its category is blocked,
        and the time {child.name} spends in it counts toward the attendance estimate.
      </p>
      <div className="block-categories" role="group" aria-label="Block categories">
        {categories.map((c) => {
          const on = chosen.includes(c.id);
          return (
            <label key={c.id} className={`check block-category ${on ? 'on' : ''}`}>
              <input type="checkbox" checked={on} disabled={busy} onChange={() => toggleCategory(c.id)} />
              <span className="block-category-emoji" aria-hidden="true">
                {c.emoji}
              </span>
              <span>
                <strong>{c.label}</strong>
                <span className="muted small block">{c.description}</span>
              </span>
            </label>
          );
        })}
      </div>

      <h3 className="with-icon">
        <AppWindow size={16} /> Apps on {child.name}’s computers
      </h3>
      {seen === null ? (
        <p className="muted small">Loading…</p>
      ) : !rows.length ? (
        <p className="muted small">
          No apps seen yet. As {child.name} uses their computer, the apps they open show up here so you can block any of them (needs the ClassGuardian app
          v0.1.54 or later).
        </p>
      ) : (
        <ul className="app-list">
          {rows.map((a) => {
            const blocked = single.some((s) => same(s, a));
            const isLearning = learning.some((s) => same(s, a));
            const cover = isLearning ? undefined : coveredBy(a);
            return (
              <li key={`${a.platform}|${a.process}`}>
                <span className="grow">
                  <strong>{a.name}</strong>
                  <span className="muted small block">
                    {a.platform === 'mac' ? 'Mac' : 'Windows'} · {a.process}
                    {a.lastSeen ? ` · seen ${relative(a.lastSeen)}` : ''}
                  </span>
                </span>
                <span className="row-gap app-actions">
                  {isLearning ? (
                    <span className="badge good">
                      <ShieldCheck size={13} /> Approved
                    </span>
                  ) : cover ? (
                    <span className="badge">
                      <Check size={13} /> Blocked with {cover.label}
                    </span>
                  ) : (
                    <button
                      className={`btn small ${blocked ? 'danger-text' : ''}`}
                      disabled={busy}
                      onClick={() => toggleApp({ name: a.name, process: a.process, platform: a.platform })}
                      aria-pressed={blocked}
                    >
                      {blocked ? 'Unblock' : 'Block'}
                    </button>
                  )}
                  <button
                    className="btn small"
                    disabled={busy}
                    onClick={() => toggleLearning({ name: a.name, process: a.process, platform: a.platform })}
                    aria-pressed={isLearning}
                    aria-label={isLearning ? `${a.name} is approved. Remove approval` : `Approve ${a.name}`}
                  >
                    {isLearning ? 'Remove approval' : 'Approve'}
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
